import { inject } from '@angular/core';
import type { CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { CoreOnboardingApiService } from '../api/core-onboarding-api.service';
import { ToastrService } from '../../shared/ui';
import { AuthStore } from './auth.store';

export const authGuard: CanActivateFn = (_route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  return authStore.ensureSession().pipe(
    map((isAuthenticated) => {
      if (!isAuthenticated) {
        return createLoginRedirect(router, state);
      }

      if (authStore.isBusinessSetupRequired() && !isOnboardingRoute(state.url)) {
        return router.parseUrl('/onboarding');
      }

      // Perfil > "Criar empresa" (W1-7) manda pra /onboarding?mode=additional mesmo com um
      // business ATIVO na sessão — o usuário já passou pelo setup, só quer uma organização a
      // MAIS. Sem essa exceção, isBusinessSetupRequired()=false bateria de volta pra '/' antes
      // da página nem carregar (ver business-onboarding.page.ts::isAdditionalWorkspace, que troca
      // o fluxo de "completar o business pendente" para "sempre criar um novo").
      if (!authStore.isBusinessSetupRequired() && isOnboardingRoute(state.url) && !isAdditionalWorkspaceOnboarding(state.url)) {
        return router.parseUrl('/');
      }

      return true;
    }),
  );
};

export const guestOnlyGuard: CanActivateFn = (route) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  return authStore
    .ensureSession()
    .pipe(
      map((isAuthenticated) =>
        isAuthenticated
          ? router.parseUrl(
              authStore.isBusinessSetupRequired()
                ? '/onboarding'
                : resolveAppReturnPath(route.queryParamMap.get('returnTo')),
            )
          : true,
      ),
    );
};

/**
 * /aceitar-convite is the one auth route that must work BOTH anonymous and already-authenticated
 * (a user who already has an account elsewhere and gets invited to a second business). Unlike
 * guestOnlyGuard, it never bounces an authenticated visitor away — it accepts the invite right
 * here and lands them in the newly-attached business, skipping the LoginPage's accept-invite
 * mode entirely (that mode only exists to route an ANONYMOUS visitor to signup/login).
 */
export const acceptInviteGuard: CanActivateFn = (route) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const onboardingApi = inject(CoreOnboardingApiService);
  const toastr = inject(ToastrService);

  return authStore.ensureSession().pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return of(true);
      }

      const token = route.queryParamMap.get('token');
      if (!token) {
        return of(router.parseUrl('/'));
      }

      return onboardingApi.acceptInvitation({ token }).pipe(
        catchError(() => {
          toastr.error(
            'Não foi possível aceitar o convite agora.',
            'Tente novamente pelo link do e-mail.',
          );
          return of(null);
        }),
        switchMap(() => authStore.loadSession()),
        map(() => router.parseUrl(authStore.isBusinessSetupRequired() ? '/onboarding' : '/')),
      );
    }),
  );
};

export function createLoginRedirect(router: Router, state: RouterStateSnapshot) {
  return router.createUrlTree(['/login'], {
    queryParams: {
      returnTo: state.url,
    },
  });
}

export function resolveAppReturnPath(value: string | null) {
  if (!value) {
    return '/';
  }

  try {
    const url = new URL(value, globalThis.location.origin);

    if (url.origin !== globalThis.location.origin) {
      return '/';
    }

    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return '/';
  }
}

function isOnboardingRoute(url: string) {
  return url === '/onboarding' || url.startsWith('/onboarding?') || url.startsWith('/onboarding#');
}

function isAdditionalWorkspaceOnboarding(url: string): boolean {
  const queryString = url.split('?')[1]?.split('#')[0] ?? '';
  return new URLSearchParams(queryString).get('mode') === 'additional';
}
